<template>
  <div>
    <h1 class="text-3xl font-bold mb-6 text-center text-gray-800">Final Review</h1>
    <p class="text-center text-gray-600 mb-4">
      Review the final questions and answers. Click any answer or point value to edit it directly.
    </p>

    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center py-10">
      <i class="pi pi-spin pi-spinner" style="font-size: 2rem"></i>
      <p class="mt-2 text-gray-600">Loading processed data...</p>
    </div>

    <!-- Error Message -->
    <div v-else-if="error" class="text-center text-red-500 bg-red-50 p-4 rounded">
      <p>Failed to load the data. Please try back and starting the processing step again.</p>
      <p class="text-sm text-red-700 mt-1">{{ error.message }}</p>
    </div>

    <!-- PrimeVue DataTable -->
    <div v-else class="card rounded-lg shadow-sm overflow-hidden">
      <DataTable
          v-model:editingRows="editingRows"
          :value="rowData"
          dataKey="number"
          editMode="cell"
          @cell-edit-complete="onCellEditComplete"
          @cell-edit-init="onCellEditInit"
          tableClass="editable-cells-table"
          showGridlines
          stripedRows
      >
        <Column
            field="number"
            header="Question #"
            style="width: 10%"
            headerClass="padded-header"
            bodyClass="padded-cell"
        >
          <template #body="{ data }">
            <span class="font-mono">{{ data.number }}</span>
          </template>
        </Column>

        <Column
            field="question"
            header="Question"
            style="width: 40%"
            headerClass="padded-header"
            bodyClass="padded-cell"
        >
          <template #body="{ data }">
            <div class="whitespace-pre-wrap">{{ data.question }}</div>
          </template>
        </Column>

        <Column
            field="answer"
            header="Answer"
            style="width: 40%"
            headerClass="padded-header"
            bodyClass="padded-cell"
        >
          <template #editor="{ data, field }">
            <Textarea
                v-model="data[field]"
                rows="5"
                class="w-full"
                autoResize
            />
          </template>
          <template #body="{ data }">
            <div class="whitespace-pre-wrap">{{ data.answer }}</div>
          </template>
        </Column>

        <Column
            field="points"
            header="Points"
            style="width: 10%"
            headerClass="padded-header"
            bodyClass="padded-cell"
        >
          <template #editor="{ data, field }">
            <InputNumber
                v-model="data[field]"
                mode="decimal"
                inputClass="w-full"
            />
          </template>
        </Column>
      </DataTable>
    </div>

    <!-- Navigation Buttons -->
    <div class="flex justify-between mt-8">
      <button
          @click="goBack"
          class="bg-gray-500 text-white px-4 py-2 rounded transition hover:bg-gray-600 flex items-center min-w-[120px] justify-center"
          :disabled="navLoading"
      >
        Back
      </button>

      <button
          @click="triggerNext"
          class="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition flex items-center min-w-[120px] justify-center"
          :disabled="navLoading"
      >
        <i v-if="navLoading" class="pi pi-spin pi-spinner mr-2"></i>
        <span v-if="navLoading">Saving...</span>
        <span v-else>Finish</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import axios from '../services/axiosInstance'

// PrimeVue components
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Textarea from 'primevue/textarea' // For PrimeVue v3 use: import InputTextarea from 'primevue/inputtextarea'
import InputNumber from 'primevue/inputnumber'

const router = useRouter()
const route = useRoute()
const testId = route.params.testId

const rowData = ref([])
const editingRows = ref([])
const loading = ref(true)
const navLoading = ref(false)
const error = ref(null)

const fetchProcessedData = async () => {
  if (!testId) {
    error.value = new Error('Test ID is missing from the URL.')
    loading.value = false
    return
  }
  try {
    const response = await axios.get(`/tests/${testId}/singles`)
    rowData.value = response.data
  } catch (err) {
    console.error('Error fetching processed data:', err)
    error.value = err
  } finally {
    loading.value = false
  }
}

const focusActiveEditor = () => {
  // Find the currently visible cell editor input/textarea and focus it
  const editor = document.querySelector('.p-cell-editing textarea, .p-cell-editing input'
  )
  if (editor) {
    editor.focus()
    if (typeof editor.setSelectionRange === 'function') {
      const len = editor.value?.length ?? 0
      editor.setSelectionRange(len, len)
    }
  }
}

const onCellEditInit = async () => {
  await nextTick()
  focusActiveEditor()
}

const onCellEditComplete = (event) => {
  const { data, newValue, field } = event
  data[field] = newValue
}

const goBack = () => {
  router.go(-1)
}

const triggerNext = async () => {
  if (!testId) {
    alert('Could not find Test ID. Cannot proceed.')
    return
  }

  navLoading.value = true

  const payload = rowData.value.map(row => ({
    number: row.number,
    question: row.question,
    answer: row.answer,
    points: row.points,
    category: row.category
  }))

  try {
    await axios.put(`/tests/${testId}/singles`, payload)
    router.push(`/tests/${testId}`)
  } catch (err) {
    alert('An error occurred while saving your final edits.')
    console.error('Error in final save:', err)
  } finally {
    navLoading.value = false
  }
}

onMounted(fetchProcessedData)
</script>

<style>
/* Give the table its own clean outline and make borders meet perfectly */
.p-datatable table {
  border-collapse: collapse;
  width: 100%;
  border: 1px solid #e5e7eb; /* gray-200 */
}

/* Round the outer corners and clip inner content so lines touch neatly */
.p-datatable,
.p-datatable .p-datatable-wrapper {
  border-radius: 0.5rem; /* matches rounded-lg */
  overflow: hidden;
}

/* Consistent borders + padding for all header/body cells */
.p-datatable .p-datatable-thead > tr > th,
.p-datatable .p-datatable-tbody > tr > td {
  border: 1px solid #e5e7eb !important;
  padding: 0.75rem 1rem; /* 12px top/bottom, 16px left/right */
}

/* Style the table header */
.p-datatable .p-datatable-thead > tr > th {
  background-color: #f9fafb; /* light gray */
  color: #374151;            /* gray-700 */
  font-weight: 600;
  border-bottom: 2px solid #e5e7eb;
}

/* Optional: add a bit of left/right padding via classes if you want */
.padded-header {
  padding-left: 1rem !important;
  padding-right: 1rem !important;
}
.padded-cell {
  padding-left: 1rem !important;
  padding-right: 1rem !important;
}

/* Make editable cells look more "editable" on hover */
.editable-cells-table .p-editable-column:hover {
  background-color: #f0f8ff; /* light blue hint */
  cursor: pointer;
}
</style>
